import React from "react";
import { map, get, isEmpty, camelCase, lowerCase, isNil } from "lodash";
import moment from "moment";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import classnames from "classnames";
import { Modal, Col, Row } from "react-bootstrap";

import Button from "components/Button";
import Alert from "components/Alert";
import SelectContributionBox from "./SelectContributionBox";
import IconSpinner from "components/IconSpinner";
import type { Schedule } from "./scheduleTypes";

import { formatCurrency } from "utils/number";
import { makeOnboardingRecurringContribution } from "actions/contributionActions";
import { contributionConstants, userConstants } from "actions/types";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { getUserState } from "actions/userActions";
import {
  DAILY,
  MONTHLY,
  END_OF_MONTH,
  payrollPeriodsToEnglishMapping,
} from "utils/timeHelper";
import { getIraAccountIdSelector } from "store/selectors/user";
import { activeBankSelector } from "store/selectors/bank";
import { getDailyToRecurringAmount } from "services/contributionService";
import { formatAmount } from "utils/number";
import type { RootState } from "types/state";

interface RecommendedContribution {
  recommended?: number;
  other?: number[];
  isMaxContributionOption?: boolean;
}

interface ContributionLimits {
  min?: number;
  max?: number;
}

interface ModifierProps {
  onSelect?: (name: string) => void;
  name?: string;
  label?: string;
  isSelected?: boolean;
}

const Modifier = ({ isSelected, name, label, onSelect }: ModifierProps) => {
  return (
    <div
      className={classnames("modifier", {
        selected: isSelected,
      })}
      onClick={() => onSelect!(name!)}
    >
      {label || name}
    </div>
  );
};

const actions = [
  contributionConstants.ONBOARDING_RECURRING_CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  userConstants.USER_STATE,
];
const errorSelector = createErrorSelector(actions);
const isSubmitting = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  const {
    dailyMax,
    weeklyMax,
    monthlyMax,
    everyOtherWeekMax,
    recommendations,
  } = state.contribution;
  const bankAccount = activeBankSelector(state);

  // contribution.recommendations is still typed `object` on RootState; the
  // reducer populates per-frequency RecommendedContribution shapes.
  const recs = recommendations as Record<
    string,
    RecommendedContribution | undefined
  >;

  return {
    dailyMax,
    weeklyMax,
    monthlyMax,
    everyOtherWeekMax,
    dailyRecommendations: recs.daily,
    weeklyRecommendations: recs.weekly,
    everyOtherWeekRecommendations: recs.everyOtherWeek,
    monthlyRecommendations: recs.monthly,
    error: errorSelector(state),
    isSubmitting: isSubmitting(state),
    accountId: getIraAccountIdSelector(state),
    bankId: get(bankAccount, "id"),
    bankAccountNumber: get(bankAccount, "accountId"),
  };
};

const mapDispatchToProps = {
  makeOnboardingRecurringContribution,
  getUserState,
  push,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // declared on the original propTypes block though never redux- or
  // parent-injected; preserved as an inert optional prop.
  isFetchingCustomAmount?: boolean;
  schedule: Schedule;
  editSchedule?: () => void;
}

type Props = OwnProps & WithApolloClient<PropsFromRedux>;

interface State {
  selectedOption: string;
  selectedCustom: boolean;
  showCustomBox: boolean;
  modifier: string;
  // Uses an empty string to clear the input between modifier screens
  customAmount: string | number;
  customAmountError: string;
  isFetchingCustomAmount: boolean;
  calculatedCustomAmount: number | null;
  // read in render but only ever written via setState in _toggleConfirmationModal
  showConfirmationModal?: boolean;
}

class SelfEmployedContributionSelection extends React.PureComponent<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    const defaultModifier = DAILY;

    this.state = {
      selectedOption: "recommended",
      selectedCustom: false,
      showCustomBox: false,
      modifier: defaultModifier,
      // Uses an empty string to clear the input between modifier screens
      customAmount: "",
      customAmountError: "",
      isFetchingCustomAmount: false,
      calculatedCustomAmount: null,
    };
  }

  _submitContribution = async (
    amount: string | number,
    isDailyCustom: boolean
  ) => {
    try {
      await this.props.makeOnboardingRecurringContribution(
        this.props.client as ApolloClient<object>,
        {
          accountId: this.props.accountId as string,
          dayOne:
            this.props.schedule.dayOne === END_OF_MONTH
              ? 31
              : +this.props.schedule.dayOne!,
          frequency: this.props.schedule.frequency as string,
          anchorPayDate: this.props.schedule.anchorPayDate as string,
          bankId: this.props.bankId,
          isDailyCustom,
          amount: +(amount as number),
        }
      );
      await this.props.getUserState(this.props.client as ApolloClient<object>);
      window.analytics.track("Submitted Self Employed Contribution");
      this.props.push("/dashboard");
    } catch (error) {
      return error;
    }
  };

  _handleSubmit = () => {
    if (this.state.selectedCustom) {
      const customIsValid =
        (this.state.customAmount as number) > 0 &&
        !this.state.customAmountError;

      if (customIsValid) {
        const isDailyCustom = this.state.modifier === DAILY;
        void this._submitContribution(this.state.customAmount, isDailyCustom);
        return;
      } else {
        return;
      }
    }

    void this._submitContribution(
      get(
        (
          this.props as unknown as Record<
            string,
            RecommendedContribution | undefined
          >
        )[`${camelCase(this.props.schedule.frequency)}Recommendations`],
        this.state.selectedOption
      ),
      false
    );
  };

  _updateModifier = (modifier: string) => {
    this.setState({ modifier, customAmount: "", customAmountError: "" });
  };

  _getSelectBoxes = () => {
    const contributionLimits = this._getContributionLimits();

    const recommendedContributions =
      this.state.modifier === DAILY
        ? this.props.dailyRecommendations
        : (
            this.props as unknown as Record<
              string,
              RecommendedContribution | undefined
            >
          )[`${camelCase(this.props.schedule.frequency)}Recommendations`];

    const selectedRec = get(
      recommendedContributions,
      this.state.selectedOption,
      null
    );

    const modifier =
      this.state.modifier === DAILY
        ? this.state.modifier
        : this.props.schedule.frequency;

    const preferredSelectBox = (
      <SelectContributionBox
        key="preferred"
        amount={recommendedContributions!.recommended}
        isRecommended={true}
        // SelectContributionBox does not declare a showAsMax prop; preserved
        // from the original JS where it was passed through harmlessly.
        // @ts-expect-error -- showAsMax is not a typed prop on the child
        showAsMax={recommendedContributions!.isMaxContributionOption}
        modifier={modifier}
        isSelected={
          !this.state.selectedCustom &&
          selectedRec === recommendedContributions!.recommended
        }
        onSelect={() => this._setSelectedContribution("recommended")}
      />
    );

    const otherOptions = map(recommendedContributions!.other, (val, idx) => (
      <SelectContributionBox
        key={val}
        amount={val}
        modifier={modifier}
        isSelected={!this.state.selectedCustom && selectedRec === val}
        onSelect={() => this._setSelectedContribution(`other.${idx}`)}
      />
    ));

    const customInputbox = this.state.showCustomBox ? (
      <SelectContributionBox
        key="custom"
        amount={this.state.customAmount as number}
        customAmountError={this.state.customAmountError}
        modifier={modifier}
        isCustomAmount={true}
        isSelected={this.state.selectedCustom}
        onSelect={() => this.setState({ selectedCustom: true })}
        setCustomAmount={this._setCustomAmount}
        contributionLimits={contributionLimits}
      />
    ) : null;

    return [preferredSelectBox, ...otherOptions, customInputbox];
  };

  _getContributionLimits = (): ContributionLimits => {
    const dailyMin = 1;
    const perPayPeriodMin = 25;

    return {
      min: this.state.modifier === DAILY ? dailyMin : perPayPeriodMin,
      max: (this.state.modifier === DAILY
        ? this.props.dailyMax
        : (this.props as unknown as Record<string, number | null | undefined>)[
            `${camelCase(this.props.schedule.frequency)}Max`
          ]) as number | undefined,
    };
  };

  _setCustomAmount = (amt: number) => {
    let customAmountError = "";
    const contributionLimits = this._getContributionLimits();

    if (amt < contributionLimits.min!) {
      customAmountError = `Custom amount must be greater than or equal to ${formatCurrency(
        contributionLimits.min!
      )}`;
    } else if (amt > contributionLimits.max!) {
      customAmountError = `Custom amount must be less than or equal to ${formatCurrency(
        contributionLimits.max!
      )}`;
    }
    this.setState({
      customAmount: formatAmount(amt),
      customAmountError,
    });
  };

  _setSelectedContribution = (selectedOption: string) => {
    this.setState({
      selectedCustom: false,
      selectedOption,
      customAmount: "",
      customAmountError: "",
    });
  };

  _toggleConfirmationModal = () => {
    this.setState((state) => {
      return {
        showConfirmationModal: !state.showConfirmationModal,
      };
    });
  };

  _buildConfirmationSentence = () => {
    const { frequency, anchorPayDate } = this.props.schedule;
    let startSentence;

    let amount: number | string | null | undefined;

    if (this.state.selectedCustom) {
      amount =
        this.state.modifier === DAILY
          ? this.state.calculatedCustomAmount
          : this.state.customAmount;
    } else {
      amount = get(
        (
          this.props as unknown as Record<
            string,
            RecommendedContribution | undefined
          >
        )[`${camelCase(frequency)}Recommendations`],
        this.state.selectedOption
      );
    }

    let firstPaymentDate = anchorPayDate;
    if (frequency === MONTHLY) {
      const dayOne =
        this.props.schedule.dayOne === "End of Month"
          ? 31
          : this.props.schedule.dayOne;

      /* The monthly scheduling logic takes a bit to calculate, basically we ask the user for the day of month they want to process
         so to figure out next processing date we have to figure out if that day already passed this month if not just use current month as processing date
         otherwise we have to figure out which date it will happen next month. The logic also accounts for variable end of month dates using moment in a way that
         ensures it will always default to last day of month if day number is outside of that months range.
      */
      let anchorDate = moment(anchorPayDate);
      // this format is important because it ensures that if we are in February that it will convert Feb 31 into Feb 28 vs March 3rd
      const currentMonthProcessingDate = moment()
        .date(dayOne as number)
        .month(anchorDate.month())
        .year(anchorDate.year());
      const willProcessThisMonth = anchorDate.isBefore(
        currentMonthProcessingDate.startOf("day")
      );

      let nextProcessingDate = currentMonthProcessingDate;

      if (!willProcessThisMonth) {
        const nextMonth = currentMonthProcessingDate.add(1, "months");
        nextProcessingDate = moment()
          .date(dayOne as number)
          .month(nextMonth.month())
          .year(nextMonth.year());
      }

      firstPaymentDate = nextProcessingDate.format("YYYY-MM-DD");
    }
    startSentence = `starting ${String(firstPaymentDate)}`;
    return (
      <p className="modal-text">
        By clicking continue, I authorize Icon Financial Services to debit my
        account ending in {this.props.bankAccountNumber} the amount of{" "}
        {formatCurrency(amount as number | string)}{" "}
        {lowerCase(
          (payrollPeriodsToEnglishMapping as Record<string, string>)[frequency!]
        )}{" "}
        {startSentence}. Scheduled payments may be cancelled up to 1 day in
        advance.
      </p>
    );
  };

  _getConfirmationModal = () => {
    return (
      <Modal
        centered
        show={true}
        onHide={this._toggleConfirmationModal}
        className="contribution-modal"
      >
        <Modal.Header className="header" closeButton={false}>
          <Modal.Title className="title" id="contained-modal-title-vcenter">
            Confirm recurring contribution
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {this._buildConfirmationSentence()}
          {this.props.error && <Alert type="error" msg={this.props.error} />}
        </Modal.Body>
        <Modal.Footer>
          <Button
            btnLabel="Cancel"
            name="cancel"
            color="cancel"
            onClick={this._toggleConfirmationModal}
          />
          <Button
            btnLabel="submit"
            name="submit"
            withArrow
            loading={this.props.isSubmitting}
            onClick={this._handleSubmit}
          />
        </Modal.Footer>
      </Modal>
    );
  };

  _getRecurringAmount = async () => {
    this.setState({ isFetchingCustomAmount: true });
    const customAmount = await getDailyToRecurringAmount(
      this.props.client as ApolloClient<object>,
      {
        frequency: this.props.schedule.frequency as string,
        amount: +(this.state.customAmount as number),
      }
    );
    this.setState({
      isFetchingCustomAmount: false,
      calculatedCustomAmount: customAmount,
    });

    this._toggleConfirmationModal();
  };

  render() {
    if (
      isEmpty(
        (
          this.props as unknown as Record<
            string,
            RecommendedContribution | undefined
          >
        )[`${camelCase(this.props.schedule.frequency)}Recommendations`]
      )
    ) {
      return <IconSpinner centered />;
    }

    const isDailyCustom =
      this.state.modifier === DAILY && this.state.selectedCustom;
    const onButtonClick = isDailyCustom
      ? this._getRecurringAmount
      : this._toggleConfirmationModal;
    return (
      <div>
        {this.state.showConfirmationModal && this._getConfirmationModal()}

        <div className="select-contributions-container">
          <Row>
            <Col lg={9} md={12} sm={12}>
              <div style={{ alignItems: "flex-end" }}>
                <div className="modifier-picker">
                  <Modifier
                    name={DAILY}
                    onSelect={this._updateModifier}
                    isSelected={this.state.modifier === DAILY}
                  />
                  <Modifier
                    name={this.props.schedule.frequency}
                    label={
                      (
                        payrollPeriodsToEnglishMapping as Record<string, string>
                      )[this.props.schedule.frequency!]
                    }
                    onSelect={this._updateModifier}
                    isSelected={
                      this.state.modifier === this.props.schedule.frequency
                    }
                  />
                </div>
              </div>
              {this._getSelectBoxes()}
              {!this.state.showCustomBox && (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                  onClick={() =>
                    this.setState({ showCustomBox: true, selectedCustom: true })
                  }
                  className="icon-link"
                >
                  Enter Custom Amount
                </span>
              )}
            </Col>
          </Row>
        </div>

        <div className="submit-row btn-row">
          <Button
            size="sm"
            color="cancel"
            name="cancel"
            onClick={this.props.editSchedule}
            btnLabel="back"
          />
          <Button
            size="sm"
            name="submit"
            onClick={onButtonClick}
            btnLabel="Confirm"
            withArrow={isDailyCustom}
            loading={this.state.isFetchingCustomAmount}
            disabled={
              this.state.selectedCustom &&
              (!isEmpty(this.state.customAmountError) ||
                isNil(this.state.customAmount) ||
                this.state.customAmount === "")
            }
          />
        </div>
      </div>
    );
  }
}

export default connector(withApollo<Props>(SelfEmployedContributionSelection));
